import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpRight, ChevronsUpDown, ExternalLink, Globe, SearchX } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Marker, MarkerContent, MarkerIcon } from '@/components/ui/marker';
import { InfoTip, Insight, InsightList, TermLabel } from '../../../components/design-system/analytics';
import { EmptyState, formatCurrency, formatDate, formatNumber } from '../../../components/design-system/admin';
import { MarketData } from '../../../common/hooks/use-market-api.hook';
import { InsightSpec, Msg, Param } from '../../../modules/vendor/market/insight-spec';
import { cleanDomain, externalHref, monthLabel } from '../../../modules/vendor/market/market-presentation';

/* ─────────────────────────────── words ─────────────────────────────── */

/**
 * Puts a finding into the reader's language: each value formatted for its
 * kind (numbers grouped, money in its currency, months named), then the key
 * translated with them.
 */
export function useSpecText() {
    const { t, i18n } = useTranslation();
    return useCallback(
        (m: Msg | undefined | null): string => {
            if (!m) return '';
            const values: Record<string, string | number> = {};
            for (const [k, v] of Object.entries(m.values ?? {})) values[k] = formatParam(v, (key, o) => String(t(key, o)), i18n.language);
            return t(m.key, { ...values, ...(m.count != null ? { count: m.count } : {}) }) as string;
        },
        [t, i18n.language],
    );
}

function formatParam(v: Param, t: (k: string, o?: any) => string, locale: string): string | number {
    // A fraction gets one decimal while it is small (2.4), none once it is not (519, never 519.4).
    if (typeof v === 'number') return formatNumber(v, Number.isInteger(v) || Math.abs(v) >= 10 ? 0 : 1);
    if (typeof v === 'string') return v;
    if ('money' in v) return formatCurrency(v.money, v.currency ?? 'usd', v.digits ?? 0);
    if ('pct' in v) return `${formatNumber(Math.round(v.pct))}%`;
    if ('month' in v) return monthLabel(v.month, locale);
    if ('date' in v) return formatDate(v.date);
    if ('tkey' in v) return t(v.tkey, { defaultValue: v.fallback ?? v.tkey });
    if ('list' in v) {
        const items = v.list.map((k) => t(k));
        try {
            return new (Intl as any).ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
        } catch {
            return items.join(', ');
        }
    }
    return '';
}

/** Findings as rows for the InsightList; an outside link opens in a new tab. */
export function useInsights(specs: InsightSpec[]): Insight[] {
    const text = useSpecText();
    return useMemo(
        () =>
            specs.map((s) => ({
                id: s.id,
                tone: s.tone,
                title: text(s.title),
                detail: s.detail ? text(s.detail) : undefined,
                action: s.action
                    ? /^https?:\/\//.test(s.action.href)
                        ? { label: text(s.action.label), onClick: () => window.open(s.action!.href, '_blank', 'noopener,noreferrer') }
                        : { label: text(s.action.label), href: s.action.href }
                    : undefined,
            })),
        [specs, text],
    );
}

/** The reading that opens a page. Nothing at all when there is nothing to say. */
export const Reading: React.FC<{ specs: InsightSpec[]; title?: string; description?: string; testId?: string }> = ({ specs, title, description, testId }) => {
    const { t } = useTranslation();
    const insights = useInsights(specs);
    return (
        <InsightList
            title={title ?? t('BUSINESS.COMMON.READING')}
            description={description ?? t('BUSINESS.COMMON.READING_HINT')}
            insights={insights}
            testId={testId ?? 'business-reading'}
        />
    );
};

export type TermKey =
    | 'DIFFICULTY'
    | 'TRAFFIC_VALUE'
    | 'REFERRING_DOMAINS'
    | 'INTENT'
    | 'AUTHORITY'
    | 'CPC'
    | 'VISITS'
    | 'POSITION'
    | 'SHARED'
    | 'SPAM'
    | 'NOFOLLOW'
    | 'OPPORTUNITY'
    | 'PAGE_TWO'
    | 'SHARE_OF_VOICE'
    | 'BACKLINKS'
    | 'HEALTH'
    | 'LCP'
    | 'CLS'
    | 'TTI'
    | 'DOM';

/** A piece of jargon with its meaning a hover away (BUSINESS.TERMS.<KEY>). */
export const Term: React.FC<{ k: TermKey; children?: ReactNode; className?: string }> = ({ k, children, className }) => {
    const { t } = useTranslation();
    const name = children ?? t(`BUSINESS.TERMS.${k}.LABEL`);
    return (
        <TermLabel
            className={className}
            label={t('BUSINESS.COMMON.WHAT_IS', { term: t(`BUSINESS.TERMS.${k}.LABEL`) })}
            tip={t(`BUSINESS.TERMS.${k}.TIP`)}
        >
            {name}
        </TermLabel>
    );
};

/* ─────────────────────────────── numbers ─────────────────────────────── */

/** DataForSEO prices traffic and clicks in US dollars, whatever the market. */
export const usd = (v: number | null | undefined, digits = 0) => (v == null ? '—' : formatCurrency(v, 'usd', digits));

/** A percent change with its sign, for small inline deltas. */
export const Delta: React.FC<{ value: number | null | undefined; invert?: boolean; className?: string; suffix?: string }> = ({
    value,
    invert,
    className,
    suffix = '%',
}) => {
    if (value == null || !Number.isFinite(value) || Math.round(value) === 0) return null;
    const up = value > 0;
    const good = invert ? !up : up;
    return (
        <span className={cn('inline-flex items-center gap-0.5 text-xs tabular-nums', good ? 'text-success' : 'text-destructive', className)}>
            {up ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
            {formatNumber(Math.abs(Math.round(value)))}
            {suffix}
        </span>
    );
};

/* ─────────────────────────────── page shell ─────────────────────────────── */

/** Where the numbers came from and when — a page of figures owes the reader this. */
export const Provenance: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t, i18n } = useTranslation();
    const country = data.country ? countryName(data.country, i18n.language) : null;
    return (
        <Marker variant="border" className="pb-3">
            <MarkerIcon>
                <Globe />
            </MarkerIcon>
            <MarkerContent>
                {t('MARKET.MEASURED', {
                    domain: cleanDomain(data.target) || '—',
                    month: data.month ? monthLabel(data.month, i18n.language) : '—',
                })}
                {country && ` · ${t('BUSINESS.COMMON.IN_MARKET', { country })}`}
                {data.plan?.limited && (
                    <span className="block text-xs text-muted-foreground" data-testid="market-plan-limited">
                        {t('FREE_PLAN.MARKET.LIMITED')}
                    </span>
                )}
            </MarkerContent>
        </Marker>
    );
};

const countryName = (code: string, locale: string): string => {
    try {
        return new (Intl as any).DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code.toUpperCase();
    } catch {
        return code.toUpperCase();
    }
};

export const NotAnalysed: React.FC<{ onGo: () => void }> = ({ onGo }) => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col items-start gap-4 rounded-xl border bg-card p-8" data-testid="business-not-analysed">
            <div>
                <p className="text-[15px] font-medium">{t('MARKET.EMPTY_TITLE')}</p>
                <p className="mt-1.5 max-w-[520px] text-sm leading-relaxed text-muted-foreground">{t('MARKET.EMPTY_BODY')}</p>
            </div>
            <Button variant="outline" size="lg" onClick={onGo}>
                {t('MARKET.EMPTY_ACTION')}
                <ArrowUpRight data-icon="inline-end" className="rtl:-scale-x-100" />
            </Button>
        </div>
    );
};

/** A part of the analysis that has not been bought or read yet. */
export const Nothing: React.FC<{ title: string; description?: string; action?: ReactNode }> = ({ title, description, action }) => (
    <div className="rounded-xl border border-dashed bg-card">
        <EmptyState icon={<SearchX />} title={title} description={description} action={action} />
    </div>
);

/** A link out of the app, with the arrow that says so. */
export const OutLink: React.FC<{ href: string; children: ReactNode; className?: string; title?: string }> = ({ href, children, className, title }) => (
    <a
        href={externalHref(href)}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        className={cn('inline-flex min-w-0 items-center gap-1 hover:underline', className)}
    >
        <span className="truncate">{children}</span>
        <ExternalLink className="size-3 shrink-0 opacity-60" aria-hidden />
    </a>
);

/* ─────────────────────────────── tables ─────────────────────────────── */

export type SortDir = 'asc' | 'desc';

/** Sort state for a table: clicking a column sorts by it, clicking again flips it. */
export function useSort<K extends string>(initial: K, dir: SortDir = 'desc') {
    const [sort, setSort] = useState<{ key: K; dir: SortDir }>({ key: initial, dir });
    const toggle = useCallback(
        (key: K, firstDir: SortDir = 'desc') =>
            setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: firstDir })),
        [],
    );
    return { sort, toggle };
}

/** Sorts a copy; missing values always go last, whichever way. */
export function sortRows<T>(rows: T[], get: (row: T) => number | string | null | undefined, dir: SortDir): T[] {
    return [...rows].sort((a, b) => {
        const x = get(a);
        const y = get(b);
        if (x == null && y == null) return 0;
        if (x == null) return 1;
        if (y == null) return -1;
        const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
        return dir === 'asc' ? c : -c;
    });
}

/**
 * A sortable column header. The explanation (when the column is jargon) sits
 * beside the sort button rather than inside it — a button in a button is not
 * something a screen reader can use.
 */
export const SortHead: React.FC<{
    label: ReactNode;
    term?: TermKey;
    active: boolean;
    dir: SortDir;
    onClick: () => void;
    align?: 'start' | 'end';
    className?: string;
}> = ({ label, term, active, dir, onClick, align = 'end', className }) => {
    const { t } = useTranslation();
    return (
        <th
            className={cn('p-3 font-medium whitespace-nowrap', align === 'end' ? 'text-end' : 'text-start', className)}
            aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <span className={cn('inline-flex items-center gap-1', align === 'end' && 'justify-end')}>
                <button
                    type="button"
                    onClick={onClick}
                    className={cn('inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none', active && 'text-foreground')}
                >
                    {label}
                    {active ? dir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" /> : <ChevronsUpDown className="size-3 opacity-40" />}
                </button>
                {term && (
                    <InfoTip label={t('BUSINESS.COMMON.WHAT_IS', { term: t(`BUSINESS.TERMS.${term}.LABEL`) })}>{t(`BUSINESS.TERMS.${term}.TIP`)}</InfoTip>
                )}
            </span>
        </th>
    );
};

/** Plain header cell, same padding as a sortable one. */
export const Head: React.FC<{ children?: ReactNode; term?: TermKey; align?: 'start' | 'end'; className?: string }> = ({ children, term, align = 'end', className }) => {
    const { t } = useTranslation();
    return (
        <th className={cn('p-3 font-medium whitespace-nowrap', align === 'end' ? 'text-end' : 'text-start', className)}>
            <span className={cn('inline-flex items-center gap-1', align === 'end' && 'justify-end')}>
                {children}
                {term && (
                    <InfoTip label={t('BUSINESS.COMMON.WHAT_IS', { term: t(`BUSINESS.TERMS.${term}.LABEL`) })}>{t(`BUSINESS.TERMS.${term}.TIP`)}</InfoTip>
                )}
            </span>
        </th>
    );
};

/** The frame every detail table sits in: scrolls sideways inside its card, never the page. */
export const TableFrame: React.FC<{ children: ReactNode; className?: string; testId?: string }> = ({ children, className, testId }) => (
    <div className={cn('overflow-x-auto', className)} data-testid={testId}>
        <table className="w-full min-w-[640px] text-sm">{children}</table>
    </div>
);
